// Phase I3.A — useSuperAccaToday hook.
//
// Reads everything for today's published Super Acca slip and joins
// platform-intelligence outputs (referee + corners + player DNA),
// scoreline matrix and CLV consensus into a single view-model the
// /super-acca page consumes.
//
// Doctrine: never fabricate. When today's slip is null, every other
// field is null and the page renders the abstention/awaiting empty-state
// variant. When a sub-table is empty, the matching panel renders its
// operator-facing empty copy — never invented numbers.

import { useCallback, useMemo } from "react";
import { useComposedQuery } from "../data/useTableQuery.ts";
import {
  adaptDisciplinary,
  adaptPlayerPerformance,
  adaptScorelineMatrix,
  adaptSlip,
  adaptTerritorial,
  deriveMarkets,
  type DerivedMarketsView,
  type DisciplinaryIntelView,
  type PlayerPerformanceView,
  type ScorelineCell,
  type SharpMoneyView,
  type SuperAccaSlip,
  type TerritorialIntelView,
} from "./view-models.ts";

export interface FixtureMeta {
  home: string;
  away: string;
  competition: string | null;
  kickoff_at: string | null;
  league: string | null;
  fixture_id: string;
}

export interface SuperAccaViewModel {
  slip: SuperAccaSlip | null;
  fixture: FixtureMeta | null;
  disciplinary: DisciplinaryIntelView | null;
  territorial: TerritorialIntelView | null;
  playerPerformance: PlayerPerformanceView | null;
  scorelineMatrix: ScorelineCell[];
  derivedMarkets: DerivedMarketsView;
  sharpMoney: SharpMoneyView;
  /** Recent/yesterday-landed teaser (most recent settled non-pending row). */
  recentResult: SuperAccaSlip | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const EMPTY_DERIVED: DerivedMarketsView = {
  over_under: null,
  btts: null,
  ah: null,
  draw: null,
  htft: null,
};

const EMPTY_SHARP: SharpMoneyView = {
  signal: null,
  open: null,
  now: null,
  drift_pp: null,
};

interface SuperAccaComposite {
  slip: SuperAccaSlip | null;
  fixture: FixtureMeta | null;
  disciplinary: DisciplinaryIntelView | null;
  territorial: TerritorialIntelView | null;
  playerPerformance: PlayerPerformanceView | null;
  scorelineMatrix: ScorelineCell[];
  derivedMarkets: DerivedMarketsView;
  recentResult: SuperAccaSlip | null;
  error: string | null;
}

const EMPTY_COMPOSITE: SuperAccaComposite = {
  slip: null,
  fixture: null,
  disciplinary: null,
  territorial: null,
  playerPerformance: null,
  scorelineMatrix: [],
  derivedMarkets: EMPTY_DERIVED,
  recentResult: null,
  error: null,
};

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export function useSuperAccaToday(): SuperAccaViewModel {
  const today = todayStr();

  const q = useComposedQuery<SuperAccaComposite>(
    ["super-acca-today", today],
    async (client): Promise<SuperAccaComposite> => {
      // db/types.ts may not include all of the new tables; cast.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = client as any;

      // Local error accumulator preserves the original semantics: the first
      // error wins, and a later error is only surfaced if none is set yet.
      let errorMsg: string | null = null;

      // 1) Today's slip + recent settled teaser.
      const [slipRes, recentRes] = await Promise.all([
        sb.from("bet_builder_slips").select("*").eq("pick_date", today).maybeSingle(),
        sb
          .from("bet_builder_slips")
          .select("*")
          .eq("status", "published")
          .neq("outcome", "pending")
          .not("outcome", "is", null)
          .order("pick_date", { ascending: false })
          .limit(1),
      ]);

      if (slipRes.error) errorMsg = slipRes.error.message;
      if (recentRes.error && !errorMsg) errorMsg = recentRes.error.message;

      const todaySlip = adaptSlip(slipRes.data ?? null);
      const recentResult = adaptSlip(
        Array.isArray(recentRes.data) ? recentRes.data[0] ?? null : null,
      );

      // The fixture identifier we read all sub-tables against. When today's
      // slip exists we use its fixture; otherwise we fall back to the
      // top-fixture RPC so the page can render a header even on abstain days.
      let fixtureId: string | null = todaySlip?.fixture_id ?? null;

      if (!fixtureId) {
        // Try the helper RPC (added in I0/I1) — silent on failure.
        try {
          const rpcRes = await sb.rpc("super_acca_pick_top_fixture", {});
          const rpcRow = Array.isArray(rpcRes.data) ? rpcRes.data[0] : rpcRes.data;
          if (rpcRow && typeof rpcRow === "object" && typeof rpcRow.fixture_id === "string") {
            fixtureId = rpcRow.fixture_id;
          }
        } catch {
          // ignore; abstain page just renders without a header fixture
        }
      }

      if (!fixtureId) {
        return {
          slip: todaySlip,
          fixture: null,
          disciplinary: null,
          territorial: null,
          playerPerformance: null,
          scorelineMatrix: [],
          derivedMarkets: EMPTY_DERIVED,
          recentResult,
          error: errorMsg,
        };
      }

      // 2) Fixture metadata. Prefer the published view over canonical_fixtures
      // because it has team display names + competition_name pre-joined.
      const fixRes = await sb
        .from("vw_pipeline_fixtures")
        .select("fixture_id, home_team, away_team, league_name, competition, kickoff_at")
        .eq("fixture_id", fixtureId)
        .maybeSingle();
      const fixRow = fixRes.data ?? null;
      let fixture: FixtureMeta | null;
      if (fixRow && typeof fixRow === "object") {
        const r = fixRow as Record<string, unknown>;
        fixture = {
          fixture_id: fixtureId,
          home: typeof r.home_team === "string" ? r.home_team : "—",
          away: typeof r.away_team === "string" ? r.away_team : "—",
          competition: typeof r.competition === "string" ? r.competition : null,
          kickoff_at: typeof r.kickoff_at === "string" ? r.kickoff_at : null,
          league: typeof r.league_name === "string" ? r.league_name : null,
        };
      } else if (todaySlip) {
        // Fall back to slip's stored fixture_name.
        fixture = {
          fixture_id: fixtureId,
          home: todaySlip.fixture_name?.split(/ vs | v | – |—/)[0] ?? "—",
          away: todaySlip.fixture_name?.split(/ vs | v | – |—/)[1] ?? "—",
          competition: todaySlip.league,
          kickoff_at: todaySlip.kickoff_at,
          league: todaySlip.league,
        };
      } else {
        fixture = null;
      }

      // 3) Sub-intelligence + scoreline matrix in parallel.
      const [discRes, terrRes, ppRes, csRes] = await Promise.all([
        sb
          .from("super_acca_disciplinary")
          .select("*")
          .eq("fixture_id", fixtureId)
          .order("slip_date", { ascending: false })
          .limit(1)
          .maybeSingle(),
        sb
          .from("super_acca_territorial")
          .select("*")
          .eq("fixture_id", fixtureId)
          .order("slip_date", { ascending: false })
          .limit(1)
          .maybeSingle(),
        sb
          .from("super_acca_player_performance")
          .select("*")
          .eq("fixture_id", fixtureId)
          .order("slip_date", { ascending: false })
          .limit(1)
          .maybeSingle(),
        sb
          .from("correct_score_insights")
          .select("*")
          .eq("fixture_id", fixtureId)
          .order("pick_date", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      const matrix = adaptScorelineMatrix(csRes.data ?? null);

      return {
        slip: todaySlip,
        fixture,
        disciplinary: adaptDisciplinary(discRes.data ?? null),
        territorial: adaptTerritorial(terrRes.data ?? null),
        playerPerformance: adaptPlayerPerformance(ppRes.data ?? null),
        scorelineMatrix: matrix,
        derivedMarkets: deriveMarkets(matrix, todaySlip),
        recentResult,
        error: errorMsg,
      };
    },
  );

  const refetch = useCallback(async (): Promise<void> => {
    await q.refetch();
  }, [q]);

  return useMemo<SuperAccaViewModel>(() => {
    const c = q.data ?? EMPTY_COMPOSITE;
    return {
      slip: c.slip,
      fixture: c.fixture,
      disciplinary: c.disciplinary,
      territorial: c.territorial,
      playerPerformance: c.playerPerformance,
      scorelineMatrix: c.scorelineMatrix,
      derivedMarkets: c.derivedMarkets,
      sharpMoney: c.slip?.sharp_money ?? EMPTY_SHARP,
      recentResult: c.recentResult,
      loading: q.isLoading,
      error: c.error,
      refetch,
    };
  }, [q.data, q.isLoading, refetch]);
}
